import React, { useState } from 'react';
import {
  ClipboardDocumentListIcon,
  EyeIcon,
  TrashIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { mockParserApi } from '../../services/mockParserApi';

const MODE_COLORS = {
  pyq: 'blue',
  'ai-generated': 'purple',
  mixed: 'indigo',
};

const MODE_LABELS = {
  pyq: 'PYQ',
  'ai-generated': 'AI Generated',
  mixed: 'Mixed',
};

export default function TestList({ tests, loading, onRefresh }) {
  const [viewingTest, setViewingTest] = useState(null);
  const [deletingTestId, setDeletingTestId] = useState(null);

  const handleViewTest = async (testId) => {
    try {
      const test = await mockParserApi.getMockTest(testId);
      setViewingTest(test.data);
    } catch (error) {
      toast.error('Failed to load test details');
      console.error('Error loading test:', error);
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this test?')) {
      return;
    }

    setDeletingTestId(testId);
    try {
      await mockParserApi.deleteMockTest(testId);
      toast.success('Test deleted successfully');
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to delete test');
      console.error('Error deleting test:', error);
    } finally {
      setDeletingTestId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!tests || tests.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-500">No mock tests generated yet</p>
        <p className="text-sm text-gray-400 mt-1">
          Generate your first test using the form above
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ClipboardDocumentListIcon className="h-6 w-6 text-gray-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">
                Recent Mock Tests
              </h3>
            </div>
            <span className="text-sm text-gray-500">
              {tests.length} test{tests.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {tests.map((test) => (
            <div
              key={test.testId}
              className="p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-medium text-gray-900">{test.title}</h4>
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full bg-${
                        MODE_COLORS[test.mode]
                      }-100 text-${MODE_COLORS[test.mode]}-800`}
                    >
                      {MODE_LABELS[test.mode]}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>
                      {test.exam} - {test.paper}
                    </span>
                    <span>•</span>
                    <span>{test.totalQuestions} questions</span>
                    <span>•</span>
                    <span>{test.duration} min</span>
                    {test.createdAt && (
                      <>
                        <span>•</span>
                        <span>
                          {format(new Date(test.createdAt), 'MMM dd, yyyy')}
                        </span>
                      </>
                    )}
                  </div>
                  {test.metadata && (
                    <div className="mt-1 text-xs text-gray-400">
                      {test.metadata.pyqCount > 0 && (
                        <span>PYQ: {test.metadata.pyqCount} </span>
                      )}
                      {test.metadata.aiGeneratedCount > 0 && (
                        <span>AI: {test.metadata.aiGeneratedCount}</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleViewTest(test.testId)}
                    className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                    title="View test"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteTest(test.testId)}
                    disabled={deletingTestId === test.testId}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                    title="Delete test"
                  >
                    {deletingTestId === test.testId ? (
                      <svg
                        className="animate-spin h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <TrashIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Test Detail Modal */}
      {viewingTest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {viewingTest.title}
              </h2>
              <button
                onClick={() => setViewingTest(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-gray-500">Exam</div>
                  <div className="font-medium">{viewingTest.exam}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Mode</div>
                  <div className="font-medium">
                    {MODE_LABELS[viewingTest.mode]}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Questions</div>
                  <div className="font-medium">
                    {viewingTest.totalQuestions}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Duration</div>
                  <div className="font-medium">{viewingTest.duration} min</div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 mb-2">Questions</h3>
                {viewingTest.questions?.map((q, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <span className="font-medium text-gray-900">
                        {q.questionNumber}. {q.questionText}
                      </span>
                      {q.isAiGenerated && (
                        <span className="ml-2 px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">
                          AI
                        </span>
                      )}
                    </div>
                    <div className="space-y-1 ml-4">
                      {q.options?.map((opt) => (
                        <div
                          key={opt.key}
                          className={`text-sm ${
                            opt.key === q.correctAnswer
                              ? 'text-green-700 font-medium'
                              : 'text-gray-600'
                          }`}
                        >
                          {opt.key}. {opt.text}
                          {opt.key === q.correctAnswer && (
                            <span className="ml-2 text-green-600">✓</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
